import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import particlesConfig from '../../Shared/pr-s-black';
import { supabase } from '../../services/supabaseClient';
import './PlatformPage.scss';

/**
 * Reusable Platform Page Component
 * Used for all individual platform pages with consistent styling
 * Fetches platform items from Supabase to display in services section
 */
export default function PlatformPage({ 
  platformName, 
  platformSlug,
  tagline, 
  description, 
  features = [], 
  benefits = [],
  logoSrc = null,
  accentColor = '#FE5500'
}) {
  const [platformItems, setPlatformItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [platformColor, setPlatformColor] = useState(accentColor);

  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  // Fetch platform color and items from Supabase
  useEffect(() => {
    const fetchPlatformData = async () => {
      try {
        // Fetch platform color from Supabase (if available)
        const { data: platformData } = await supabase
          .from('platforms')
          .select('color')
          .eq('name', platformName)
          .single();
        
        if (platformData?.color) {
          setPlatformColor(platformData.color);
        }

        // Fetch items for this platform
        const { data: items, error } = await supabase
          .from('items')
          .select('id, platform, category, tier, type, price, range_min, range_max')
          .eq('platform', platformName)
          .limit(6);

        if (error) {
          console.error('Error fetching items:', error);
        } else {
          setPlatformItems(items || []);
        }
      } catch (err) {
        console.error('Error fetching platform data:', err);
      } finally {
        setLoadingItems(false);
      }
    };

    fetchPlatformData();
  }, [platformName]);

  // Format price display
  const formatPrice = (item) => {
    if (item.price) {
      return `$${item.price.toFixed(0)}`;
    }
    if (item.range_min && item.range_max) {
      return `$${item.range_min.toFixed(0)} - $${item.range_max.toFixed(0)}`;
    }
    return 'Contact for pricing';
  };

  return (
    <div className="platform-page" style={{ '--accent-color': platformColor }}>
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={particlesConfig}
        className="particles-background"
      />
      <div className="platform-content">
        {/* Hero Section with Styled Title */}
        <header className="platform-hero">
          {logoSrc ? (
            <div className="platform-logo-large">
              <img src={logoSrc} alt={`${platformName} Logo`} />
            </div>
          ) : (
            <div className="platform-logo-placeholder" style={{ 
              width: '100px', 
              height: '100px', 
              background: `linear-gradient(135deg, ${platformColor}40, ${platformColor}20)`,
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2.5rem',
              fontWeight: 'bold',
              color: platformColor,
              margin: '0 auto 30px'
            }}>
              {platformName.charAt(0)}
            </div>
          )}
          <h1 className="platform-title">
            <span className="platform-name">{platformName}</span>
            <span className="platform-subtitle">Marketing Platform</span>
          </h1>
          <p className="platform-tagline">{tagline}</p>
        </header>

        {/* Description Section */}
        <section className="platform-section">
          <h2>About {platformName} Marketing</h2>
          <p>{description}</p>
        </section>

        {/* Features Grid */}
        {features.length > 0 && (
          <section className="platform-section">
            <h2>Key Marketing Features</h2>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Benefits List */}
        {benefits.length > 0 && (
          <section className="platform-section">
            <h2>Why Choose {platformName}?</h2>
            <ul className="benefits-list">
              {benefits.map((benefit, index) => (
                <li key={index}>{benefit}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Shop Services Box */}
        <section className="platform-services-box">
          <div className="services-box-content">
            <h2>Shop {platformName} Services</h2>
            <p>Browse our marketplace for professional {platformName} marketing services from vetted vendors.</p>
            
            {/* Display items from Supabase if available */}
            {!loadingItems && platformItems.length > 0 && (
              <div className="services-items-grid">
                {platformItems.map((item) => (
                  <Link 
                    key={item.id} 
                    to={`/product/${item.category}/${item.id}`}
                    className="service-item-card"
                  >
                    <div className="item-category">{item.category}</div>
                    <div className="item-tier">{item.tier || 'Standard'}</div>
                    <div className="item-type">{item.type}</div>
                    <div className="item-price">{formatPrice(item)}</div>
                  </Link>
                ))}
              </div>
            )}

            {/* Loading state */}
            {loadingItems && (
              <div className="services-loading">Loading services...</div>
            )}

            {/* No items message */}
            {!loadingItems && platformItems.length === 0 && (
              <div className="services-empty">
                No services available yet. Check back soon or browse all services.
              </div>
            )}

            <div className="services-actions">
              <Link to={`/marketing-services?platform=${platformSlug || platformName.toLowerCase()}`} className="btn-primary">
                Browse All {platformName} Services
              </Link>
              <Link to="/marketing-genius" className="btn-secondary">
                Get AI Strategy
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
